import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Shell } from "@/components/Shell";
import { useSiteAssets } from "@/lib/siteAssets";
import { imageUrl } from "@/components/ImageUpload";
import { Mail, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const assets = useSiteAssets();

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email.trim()) return setError("Email is required.");
        setBusy(true);
        try {
            await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
            setDone(true);
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Please enter a valid email address.");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:block relative bg-brand-bone">
                <img src={imageUrl(assets.login_background)} className="absolute inset-0 w-full h-full object-cover" alt="" />
            </div>
            <div className="flex items-center justify-center p-8">
                <div className="w-full max-w-sm" data-testid="forgot-password-page">
                    <Link to="/" className="text-sm text-brand-mute hover:text-brand-forest">← Back to HealthFairX</Link>
                    <h1 className="font-heading text-3xl font-extrabold mt-6">Forgot your password?</h1>
                    {done ? (
                        <div className="mt-6 rounded-xl border border-brand-line bg-white p-6" data-testid="forgot-password-success">
                            <CheckCircle2 className="text-brand-forest" size={28} />
                            <h2 className="font-heading font-bold text-lg mt-3">Check your inbox</h2>
                            <p className="text-sm text-brand-mute mt-2">If an account exists for <strong>{email}</strong>, we just sent a password reset link. It expires in 1 hour. Don't see it? Check your spam folder.</p>
                            <Link to="/login" className="mt-5 inline-block pill btn-forest px-5 py-2.5 text-sm font-semibold">Back to login</Link>
                        </div>
                    ) : (
                        <>
                            <p className="mt-2 text-sm text-brand-mute">Enter the email you signed up with and we'll send you a secure link to choose a new password.</p>
                            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-password-form" noValidate>
                                <div>
                                    <label className="label-base">Email</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mute" size={16} />
                                        <input data-testid="forgot-email-input" type="email" autoComplete="email" required className="input-base pl-10" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
                                    </div>
                                </div>
                                {error && <div className="text-sm text-brand-coral" data-testid="forgot-error">{error}</div>}
                                <button data-testid="forgot-submit" disabled={busy} className="w-full pill btn-coral px-5 py-3 text-sm font-semibold disabled:opacity-60">{busy ? "Sending…" : "Send reset link"}</button>
                                <p className="text-xs text-brand-mute text-center">
                                    Remember your password? <Link to="/login" className="underline text-brand-forest">Log in</Link>
                                </p>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
